import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api'
import { useAuth } from '../lib/auth'
import {
  User,
  Shield,
  Key,
  Bell,
  Save,
  Plus,
  Trash2,
  Edit,
  Lock,
} from 'lucide-react'

export default function Settings() {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const [activeTab, setActiveTab] = useState<'profile' | 'users' | 'api' | 'notifications'>('profile')
  const [fullName, setFullName] = useState(user?.full_name || '')
  const [showNewUserModal, setShowNewUserModal] = useState(false)
  const [newUser, setNewUser] = useState({ email: '', password: '', full_name: '', role: 'viewer' })
  const [pwForm, setPwForm] = useState({ current_password: '', new_password: '', confirm: '' })
  const [pwMsg, setPwMsg] = useState('')
  const [editUser, setEditUser] = useState<any | null>(null)
  const [editForm, setEditForm] = useState({ full_name: '', role: 'viewer', is_active: true, new_password: '' })

  const { data: users } = useQuery({
    queryKey: ['users'],
    queryFn: () => api.get<any[]>('/auth/users'),
    enabled: user?.role === 'admin' && activeTab === 'users',
  })

  const updateProfileMutation = useMutation({
    mutationFn: (data: { full_name: string }) => api.patch('/auth/me', data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['auth'] }),
  })

  const createUserMutation = useMutation({
    mutationFn: (data: typeof newUser) => api.post('/auth/register', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
      setShowNewUserModal(false)
      setNewUser({ email: '', password: '', full_name: '', role: 'viewer' })
    },
  })

  const deleteUserMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/auth/users/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['users'] }),
  })

  const changePwMutation = useMutation({
    mutationFn: (data: { current_password: string; new_password: string }) => api.post('/auth/me/password', data),
    onSuccess: () => {
      setPwMsg('Password changed successfully.')
      setPwForm({ current_password: '', new_password: '', confirm: '' })
    },
    onError: (e: any) => setPwMsg(e.message),
  })

  const updateUserMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => api.patch(`/auth/users/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
      if (!editForm.new_password) {
        setEditUser(null)
      }
    },
  })

  const resetPwMutation = useMutation({
    mutationFn: ({ id, new_password }: { id: string; new_password: string }) =>
      api.post(`/auth/users/${id}/reset-password`, { new_password }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
      setEditUser(null)
      setEditForm({ full_name: '', role: 'viewer', is_active: true, new_password: '' })
    },
  })

  const handleProfileSave = (e: React.FormEvent) => {
    e.preventDefault()
    updateProfileMutation.mutate({ full_name: fullName })
  }

  const handleCreateUser = (e: React.FormEvent) => {
    e.preventDefault()
    createUserMutation.mutate(newUser)
  }

  const handlePwChange = (e: React.FormEvent) => {
    e.preventDefault()
    setPwMsg('')
    if (pwForm.new_password !== pwForm.confirm) {
      setPwMsg('New passwords do not match.')
      return
    }
    changePwMutation.mutate({ current_password: pwForm.current_password, new_password: pwForm.new_password })
  }

  const openEdit = (u: any) => {
    setEditUser(u)
    setEditForm({ full_name: u.full_name || '', role: u.role, is_active: u.is_active, new_password: '' })
  }

  const handleEditSave = (e: React.FormEvent) => {
    e.preventDefault()
    if (!editUser) return
    const data: any = { full_name: editForm.full_name, role: editForm.role, is_active: editForm.is_active }
    if (editForm.new_password) {
      if (editForm.new_password.length < 8) {
        alert('Password must be at least 8 characters.')
        return
      }
      resetPwMutation.mutate({ id: editUser.id, new_password: editForm.new_password })
    }
    updateUserMutation.mutate({ id: editUser.id, data })
    if (!editForm.new_password) setEditUser(null)
  }

  return (
    <div className="page">
      <div className="page-header">
        <h1>Settings</h1>
        <p>Manage your account, users, and integrations</p>
      </div>

      <div className="tabs">
        <button className={`tab ${activeTab === 'profile' ? 'active' : ''}`} onClick={() => setActiveTab('profile')}>
          <User size={16} /> Profile
        </button>
        {user?.role === 'admin' && (
          <button className={`tab ${activeTab === 'users' ? 'active' : ''}`} onClick={() => setActiveTab('users')}>
            <Shield size={16} /> Users
          </button>
        )}
        <button className={`tab ${activeTab === 'api' ? 'active' : ''}`} onClick={() => setActiveTab('api')}>
          <Key size={16} /> API Keys
        </button>
        <button className={`tab ${activeTab === 'notifications' ? 'active' : ''}`} onClick={() => setActiveTab('notifications')}>
          <Bell size={16} /> Notifications
        </button>
      </div>

      {activeTab === 'profile' && (
        <div className="card">
          <h2>Profile Settings</h2>
          <form onSubmit={handleProfileSave}>
            <div className="form-group">
              <label>Email</label>
              <input value={user?.email || ''} disabled />
              <p className="form-help">Email cannot be changed</p>
            </div>
            <div className="form-group">
              <label htmlFor="fullName">Full Name</label>
              <input id="fullName" value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder="Your name" />
            </div>
            <div className="form-group">
              <label>Role</label>
              <input value={user?.role || ''} disabled />
            </div>
            <button type="submit" className="btn btn-primary" disabled={updateProfileMutation.isPending}>
              <Save size={16} /> {updateProfileMutation.isPending ? 'Saving...' : 'Save Changes'}
            </button>
          </form>
        </div>
      )}

      {activeTab === 'profile' && (
        <div className="card">
          <h2><Lock size={16} /> Change Password</h2>
          <form onSubmit={handlePwChange}>
            <div className="form-group">
              <label>Current Password</label>
              <input type="password" value={pwForm.current_password} onChange={(e) => setPwForm({ ...pwForm, current_password: e.target.value })} required autoComplete="current-password" />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>New Password (min 8 chars)</label>
                <input type="password" value={pwForm.new_password} onChange={(e) => setPwForm({ ...pwForm, new_password: e.target.value })} required minLength={8} autoComplete="new-password" />
              </div>
              <div className="form-group">
                <label>Confirm New Password</label>
                <input type="password" value={pwForm.confirm} onChange={(e) => setPwForm({ ...pwForm, confirm: e.target.value })} required autoComplete="new-password" />
              </div>
            </div>
            {pwMsg && <p className="muted">{pwMsg}</p>}
            <button type="submit" className="btn btn-primary" disabled={changePwMutation.isPending}>
              {changePwMutation.isPending ? 'Changing...' : 'Change Password'}
            </button>
          </form>
        </div>
      )}

      {activeTab === 'users' && user?.role === 'admin' && (
        <div className="card">
          <div className="card-header">
            <h2>User Management</h2>
            <button className="btn btn-primary" onClick={() => setShowNewUserModal(true)}>
              <Plus size={16} /> Add User
            </button>
          </div>
          <div className="table-container">
            <table>
              <thead>
                <tr><th>Email</th><th>Name</th><th>Role</th><th>Status</th><th>Last Login</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {users?.map((u: any) => (
                  <tr key={u.id}>
                    <td>{u.email}</td>
                    <td>{u.full_name || '-'}</td>
                    <td><span className="badge badge-info">{u.role}</span></td>
                    <td><span className={`badge ${u.is_active ? 'badge-success' : 'badge-secondary'}`}>{u.is_active ? 'Active' : 'Inactive'}</span></td>
                    <td>{u.last_login_at ? new Date(u.last_login_at).toLocaleString() : 'Never'}</td>
                    <td>
                      <div className="action-menu">
                        <button className="icon-btn" onClick={() => openEdit(u)} title="Edit user / reset password">
                          <Edit size={16} />
                        </button>
                        <button className="icon-btn danger" onClick={() => deleteUserMutation.mutate(u.id)} disabled={u.id === user?.id} title="Delete user">
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {editUser && (
            <div className="modal-overlay" onClick={() => setEditUser(null)}>
              <div className="modal" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h2>Edit User — {editUser.email}</h2>
                  <button className="icon-btn" onClick={() => setEditUser(null)}>✕</button>
                </div>
                <form onSubmit={handleEditSave} className="modal-body">
                  <div className="form-group">
                    <label>Full Name</label>
                    <input value={editForm.full_name} onChange={(e) => setEditForm({ ...editForm, full_name: e.target.value })} />
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Role</label>
                      <select value={editForm.role} onChange={(e) => setEditForm({ ...editForm, role: e.target.value })} disabled={editUser.id === user?.id}>
                        <option value="viewer">Viewer</option>
                        <option value="editor">Editor</option>
                        <option value="admin">Admin</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Status</label>
                      <select value={editForm.is_active ? 'true' : 'false'} onChange={(e) => setEditForm({ ...editForm, is_active: e.target.value === 'true' })} disabled={editUser.id === user?.id}>
                        <option value="true">Active</option>
                        <option value="false">Inactive</option>
                      </select>
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Reset Password (blank = keep current, min 8 chars)</label>
                    <input type="password" value={editForm.new_password} onChange={(e) => setEditForm({ ...editForm, new_password: e.target.value })} placeholder="••••••••" autoComplete="new-password" />
                  </div>
                  <div className="modal-actions">
                    <button type="button" className="btn btn-secondary" onClick={() => setEditUser(null)}>Cancel</button>
                    <button type="submit" className="btn btn-primary" disabled={updateUserMutation.isPending || resetPwMutation.isPending}>
                      {(updateUserMutation.isPending || resetPwMutation.isPending) ? 'Saving...' : 'Save'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {showNewUserModal && (
            <div className="modal-overlay" onClick={() => setShowNewUserModal(false)}>
              <div className="modal" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h2>Add User</h2>
                  <button className="icon-btn" onClick={() => setShowNewUserModal(false)}>✕</button>
                </div>
                <form onSubmit={handleCreateUser} className="modal-body">
                  <div className="form-group">
                    <label>Email *</label>
                    <input type="email" value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} required />
                  </div>
                  <div className="form-group">
                    <label>Password * (min 8 chars)</label>
                    <input type="password" value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} required minLength={8} />
                  </div>
                  <div className="form-group">
                    <label>Full Name</label>
                    <input value={newUser.full_name} onChange={(e) => setNewUser({ ...newUser, full_name: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label>Role</label>
                    <select value={newUser.role} onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}>
                      <option value="viewer">Viewer</option>
                      <option value="editor">Editor</option>
                      <option value="admin">Admin</option>
                    </select>
                  </div>
                  <div className="modal-actions">
                    <button type="button" className="btn btn-secondary" onClick={() => setShowNewUserModal(false)}>Cancel</button>
                    <button type="submit" className="btn btn-primary" disabled={createUserMutation.isPending}>
                      {createUserMutation.isPending ? 'Creating...' : 'Create'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'api' && (
        <div className="card">
          <h2>API Integrations</h2>
          <p className="muted">Configure API keys via environment variables on the server (.env). Keys are never displayed here.</p>
          <div className="integration-list">
            {[
              { name: 'OpenAI', desc: 'Script generation + text-to-speech (gpt-4o-mini)', env: 'OPENAI_API_KEY' },
              { name: 'YouTube Data API', desc: 'Upload, metadata, analytics sync (OAuth)', env: 'YOUTUBE_CLIENT_ID / YOUTUBE_CLIENT_SECRET' },
              { name: 'Trends (SerpAPI / RapidAPI)', desc: 'Trending topic discovery per region', env: 'TRENDS_API_KEY' },
            ].map((i) => (
              <div key={i.name} className="integration-item">
                <Key size={20} />
                <div><h4>{i.name}</h4><p>{i.desc}</p><code>{i.env}</code></div>
              </div>
            ))}
          </div>
          <h3 style={{ marginTop: 24 }}>Multi-language & Markets</h3>
          <p className="muted">50+ TTS languages and 40+ YouTube markets are built in (see backend/app/i18n/registry.py). Per-video localizations are auto-generated in the Metadata stage.</p>
        </div>
      )}

      {activeTab === 'notifications' && (
        <div className="card">
          <h2>Notifications</h2>
          <p className="muted">Job failure / compliance blocker alerts. Webhook receiver lives at <code>/api/v1/webhooks/youtube</code>.</p>
          <div className="form-group">
            <label>Alert on job failure</label>
            <select defaultValue="dashboard"><option value="dashboard">Dashboard only</option><option value="email">Email (configure SMTP)</option></select>
          </div>
          <div className="form-group">
            <label>Alert on compliance blocker</label>
            <select defaultValue="dashboard"><option value="dashboard">Dashboard only</option><option value="email">Email (configure SMTP)</option></select>
          </div>
        </div>
      )}
    </div>
  )
}
